import { useState } from "react";
import { Shield, Activity, FileText, Clock, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { MaskText, Reveal } from "./Reveal";
import { GLP1, PAYMENT } from "../data/products";
import Glp1Questionnaire from "./Glp1Questionnaire";

const VALUE_PROPS = [
  { icon: Shield, t: "One flat fee", b: "Covers your product, ongoing check-ins and free shipping. No hidden fees, no surprises." },
  { icon: Activity, t: "Price never changes", b: "Your monthly price stays exactly the same as your dose is adjusted. Ever." },
  { icon: FileText, t: "The same active ingredient", b: "The same active ingredient used in leading GLP-1 treatments. Not FDA-approved; certificate of analysis available on request." },
  { icon: Clock, t: "24/7 support", b: "Email dose-change requests and message our support team anytime. Cancel anytime." },
];

function PlanCard({ plan, onStart }) {
  return (
    <Reveal y={40}>
      <div className="flex flex-col h-full bg-white border border-[var(--ink)]/10" data-testid={`plan-${plan.id}`}>
        <div className="px-8 pt-8">
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--terra)]">{plan.overline}</span>
        </div>
        <div className="mx-8 mt-6 aspect-[4/3] bg-[var(--bone)] border border-[var(--ink)]/5 overflow-hidden">
          <img src={plan.img} alt={plan.name} loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="p-8 flex flex-col flex-1">
          <h3 className="font-display text-4xl tracking-tight">{plan.code || plan.name}</h3>
          <p className="font-body text-sm text-[var(--ink)]/60 mt-3 leading-relaxed">{plan.tagline}</p>
          <div className="mt-6 flex items-baseline gap-1">
            <span className="font-display text-6xl leading-none">${plan.price}</span>
            <span className="font-mono text-sm text-[var(--ink)]/60">/mo flat</span>
          </div>
          <ul className="mt-6 space-y-2.5">
            {plan.perks.map((p) => (
              <li key={p} className="flex items-start gap-2.5 font-body text-sm text-[var(--ink)]/75">
                <span className="text-[var(--terra)] mt-0.5">✓</span> {p}
              </li>
            ))}
          </ul>
          <button
            onClick={() => onStart(plan)}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[var(--terra)] text-white py-4 rounded-full hover:opacity-90 transition-opacity"
            data-testid={`start-questionnaire-${plan.id}`}
          >
            Start questionnaire <ArrowRight size={16} />
          </button>
          <p className="font-mono text-[10px] tracking-wide uppercase text-[var(--ink)]/40 mt-4 text-center">
            2-minute eligibility check · no obligation
          </p>
        </div>
      </div>
    </Reveal>
  );
}

export default function Glp1Program() {
  const { addItem, setOpen } = useCart();
  const [quiz, setQuiz] = useState(null);

  const onEligible = (plan, shipDose) => {
    addItem({
      id: plan.id,
      name: `${plan.name} — Monthly`,
      variant: `$${plan.price}/mo flat`,
      price: plan.price,
      img: plan.img,
      freeShip: true,
      ship_dose: shipDose || null,
    });
    setQuiz(null);
    setTimeout(() => setOpen(true), 260);
  };

  return (
    <section id="program" className="pt-24 sm:pt-36 pb-16 sm:pb-20 bg-[var(--bone)]" data-testid="section-program">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        {/* Hero band */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-end mb-16 sm:mb-20">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-[var(--terra)] mb-6">Weight Management</p>
            <MaskText
              as="h2"
              lines={["Weight support,", "purely transparent."]}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter"
            />
          </div>
          <Reveal delay={0.15}>
            <p className="font-body text-base text-[var(--ink)]/70 leading-relaxed max-w-md lg:ml-auto">
              A simple, honest GLP-1 program. Complete a quick eligibility check, then start at one flat monthly price — the same price at every dose. No memberships, no clinic markup, cancel anytime.
            </p>
          </Reveal>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {GLP1.map((plan) => (
            <PlanCard key={plan.id} plan={plan} onStart={setQuiz} />
          ))}
        </div>

        {/* Value props grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[var(--ink)]/10 mt-16" data-testid="glp1-value-props">
          {VALUE_PROPS.map((v) => (
            <Reveal key={v.t}>
              <div className="p-8 border-b border-r border-[var(--ink)]/10 h-full">
                <v.icon size={20} className="text-[var(--ink)]" strokeWidth={1.5} />
                <h4 className="font-display text-2xl mt-5 tracking-tight">{v.t}</h4>
                <p className="font-body text-sm text-[var(--ink)]/60 mt-3 leading-relaxed">{v.b}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Retatrutide is now purchasable in the Research vials section below. */}

        <p className="font-mono text-[11px] leading-relaxed text-[var(--ink)]/40 mt-10 max-w-2xl">
          Eligibility is an automated self-screening. We are not a medical provider, clinic, or telehealth service, and this is not medical advice. Products are not FDA-approved to diagnose, treat, cure, or prevent any disease. Certificate of analysis available on request.
        </p>
      </div>

      <Glp1Questionnaire open={!!quiz} plan={quiz} onClose={() => setQuiz(null)} onEligible={onEligible} />
    </section>
  );
}
